import { Box, Typography } from '@mui/material'
import { deepPurple, lightBlue, yellow } from '@mui/material/colors'
import Paper from '@mui/material/Paper'
import ReactECharts from 'echarts-for-react'
import { DateTime } from 'luxon'
import type { FC } from 'react'
import { useMemo, useState } from 'react'

import WeekSelector from '@/features/bidding/components/capacity/offer/WeekSelector'
import { useBidOfferDataContext } from '@/features/bidding/contexts/BidOfferDataContext'
import { useInsightsDataQuery } from '@/features/bidding/hooks/useInsightsDataQuery'
import { convertToRoundedMw } from '@/features/bidding/utils/calculations/convertToRoundedMw'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

type Props = {
  activationGroupUuid: string
  priceArea: string
  deliveryDay: MarketDate
}

const CapacityOfferChart: FC<Props> = ({ activationGroupUuid, priceArea, deliveryDay }) => {
  // Read offered volumes directly from context
  const { currentPtusByActivationGroup } = useBidOfferDataContext()
  const offeredVolumes = currentPtusByActivationGroup.get(activationGroupUuid) ?? []
  const fromDeliveryDay = deliveryDay.minus({ days: 7 })
  const toDeliveryDay = deliveryDay.plus({ days: 1 })
  const [start, setStart] = useState(fromDeliveryDay)

  // Compute hourly average watts from individual PTU offered volumes
  const hourlyAverageWatts = useMemo(() => {
    if (!offeredVolumes || offeredVolumes.length === 0) return new Map<string, number>()

    const aggregate = new Map<string, { total: number; count: number }>()

    for (const ptuRow of offeredVolumes) {
      const hourKey = ptuRow.ptu.start.toUTC().startOf('hour').toISO()!
      const entry = aggregate.get(hourKey) ?? { total: 0, count: 0 }
      aggregate.set(hourKey, {
        total: entry.total + ptuRow.offeredVolume.quantity,
        count: entry.count + 1,
      })
    }

    const avgMap = new Map<string, number>()
    for (const [key, val] of aggregate.entries()) {
      avgMap.set(key, Math.round(val.total / val.count))
    }

    return avgMap
  }, [offeredVolumes])

  const { data } = useInsightsDataQuery({ activationGroupUuid, priceArea, fromDeliveryDay: start, toDeliveryDay })

  if (!data) {
    return
  }

  const itemStyles = {
    actual: { borderColor: lightBlue[700], borderWidth: 1, color: lightBlue[700] },
    offered: { borderColor: lightBlue[300], borderWidth: 1, color: lightBlue[50] },
    forecast: { borderType: 'dashed', borderColor: lightBlue[300], borderWidth: 1, color: lightBlue[300] },
    solarActual: { borderColor: yellow[700], borderWidth: 1, color: yellow[50] },
    solarForecast: { borderType: 'dashed', borderColor: yellow[700], borderWidth: 1, color: yellow[50] },
    priceActual: { borderColor: deepPurple[600], borderWidth: 1, color: deepPurple[600] },
    priceForecast: { borderType: 'dashed', borderColor: deepPurple[600], borderWidth: 1, color: deepPurple[600] },
  }
  const xAxisValueFormat = 'dd LLL, yyyy - HH:mm'
  const xAxisLabelFormat = 'ccc, dd LLL'
  const isXAxisValueStartOfDay = (value: string) =>
    DateTime.fromFormat(value, xAxisValueFormat).equals(DateTime.fromFormat(value, xAxisValueFormat).startOf('day'))
  const option = {
    color: [lightBlue[700], lightBlue[300], lightBlue[300], yellow[700], yellow[700], deepPurple[600], deepPurple[600]],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
    },
    grid: {
      left: '5%',
      right: '5%',
      top: '15%',
      bottom: '5%',
    },
    toolbox: {
      feature: {
        dataView: { show: true, readOnly: false },
        restore: { show: true },
        saveAsImage: { show: true },
      },
    },
    legend: {
      data: [
        { name: 'Maintained Capacity (MW)', icon: 'line', itemStyle: itemStyles.actual },
        {
          name: 'Offer (MW)',
          icon: 'roundRect',
          itemStyle: itemStyles.offered,
        },
        {
          name: 'Forecast (MW)',
          icon: 'line',
          itemStyle: itemStyles.forecast,
        },
        {
          name: 'Solar Actual (W/m²)',
          icon: 'roundRect',
          itemStyle: itemStyles.solarActual,
        },
        {
          name: 'Solar Forecast (W/m²)',
          icon: 'roundRect',
          itemStyle: itemStyles.solarForecast,
        },
        {
          name: 'DA Actual (€/MW)',
          icon: 'line',
          itemStyle: itemStyles.priceActual,
        },
        {
          name: 'DA Forecast (€/MW)',
          icon: 'line',
          itemStyle: itemStyles.priceForecast,
        },
      ],
      left: 16,
      top: 32,
      color: lightBlue[700],
      itemWidth: 24,
    },
    xAxis: [
      {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
          interval: (_index: any, value: string) => isXAxisValueStartOfDay(value),
        },
        axisLabel: {
          interval: (_index: any, value: string) => isXAxisValueStartOfDay(value),
          formatter: (value: string) => DateTime.fromFormat(value, xAxisValueFormat).toFormat(xAxisLabelFormat),
          hideOverlap: true,
        },
        lineStyle: {
          color: '#00000061',
        },
        boundaryGap: false,
        // prettier-ignore
        data: data.ptus.map(ptu => ptu.toFormat(xAxisValueFormat)),
      },
    ],
    yAxis: [
      {
        type: 'value',
        position: 'left',
        alignTicks: true,
        boundaryGap: false,
        axisLine: {
          show: true,
          lineStyle: {
            color: lightBlue[700],
          },
        },
        axisLabel: {
          formatter: '{value}',
        },
        axisTick: {
          show: true,
        },
      },
      {
        type: 'value',
        position: 'right',
        alignTicks: true,
        boundaryGap: false,
        axisLine: {
          show: true,
          lineStyle: {
            color: deepPurple[600],
          },
        },
        axisLabel: {
          formatter: '{value}',
        },
        axisTick: {
          show: true,
        },
      },
      {
        type: 'value',
        position: 'right',
        offset: 32,
        alignTicks: true,
        boundaryGap: false,
        axisLine: {
          show: true,
          lineStyle: {
            color: yellow[700],
          },
        },
        axisLabel: {
          formatter: '{value}',
        },
        axisTick: {
          show: true,
        },
      },
    ],
    series: [
      {
        name: 'Maintained Capacity (MW)',
        type: 'line',
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 4,
        itemStyle: itemStyles.actual,
        lineStyle: {
          color: lightBlue[700],
          width: 1,
          type: 'solid',
        },
        data: data.capacityActual.map((watts) => (watts != null ? convertToRoundedMw(watts) : null)),
      },
      {
        name: 'Offer (MW)',
        type: 'line',
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 4,
        itemStyle: itemStyles.offered,
        lineStyle: {
          color: lightBlue[300],
          width: 1,
          type: 'solid',
        },
        areaStyle: {
          color: lightBlue[50],
        },
        data: data.ptus.map((ptu) => {
          const key = ptu.toUTC().startOf('hour').toISO()!
          const watts = hourlyAverageWatts.get(key)
          return watts != null ? convertToRoundedMw(watts) : null
        }),
      },
      {
        name: 'Forecast (MW)',
        type: 'line',
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 4,
        itemStyle: itemStyles.forecast,
        lineStyle: {
          color: lightBlue[300],
          width: 1,
          type: 'dashed',
        },
        data: data.capacityForecast.map((watts) => (watts != null ? convertToRoundedMw(watts) : null)),
      },
      {
        name: 'Solar Actual (W/m²)',
        type: 'line',
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 4,
        itemStyle: itemStyles.solarActual,
        lineStyle: {
          color: yellow[700],
          width: 1,
          type: 'solid',
        },
        areaStyle: {
          color: yellow[50],
        },
        yAxisIndex: 2,
        data: data.solarActual,
      },
      {
        name: 'Solar Forecast (W/m²)',
        type: 'line',
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 4,
        itemStyle: itemStyles.solarForecast,
        lineStyle: {
          color: yellow[700],
          width: 1,
          type: 'dashed',
        },
        areaStyle: {
          color: yellow[50],
        },
        yAxisIndex: 2,
        data: data.solarForecast,
      },
      {
        name: 'DA Actual (€/MW)',
        type: 'line',
        step: 'end',
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 4,
        itemStyle: itemStyles.priceActual,
        lineStyle: {
          color: deepPurple[600],
          width: 1,
          type: 'solid',
        },
        yAxisIndex: 1,
        data: data.spotPriceActual,
      },
      {
        name: 'DA Forecast (€/MW)',
        type: 'line',
        step: 'end',
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 4,
        itemStyle: itemStyles.priceForecast,
        lineStyle: {
          color: deepPurple[600],
          width: 1,
          type: 'dashed',
        },
        yAxisIndex: 1,
        data: data.spotPriceForecast,
      },
    ],
  }

  const onWeeksChange = (weeks: number) => {
    setStart(deliveryDay.minus({ days: weeks * 7 }))
  }

  return (
    <Paper style={{ padding: 16, marginTop: 16 }}>
      <Typography variant="h1">Bid forecast & actuals</Typography>
      <Box sx={{ pt: 3, pb: 0 }}>
        <WeekSelector initialWeeks={1} onWeeksChange={onWeeksChange}></WeekSelector>
      </Box>
      <ReactECharts data-testid="capacity-offer-chart" notMerge={true} option={option} style={{ height: 500 }} />
    </Paper>
  )
}

export default CapacityOfferChart
